// src/pages/client/services/Cruise.js
import React, { useEffect, useMemo, useState } from "react";
import Container from "../../../components/Container";
import {
  CheckCircle2,
  ArrowRight,
  Ship,
  ShieldCheck,
  Clock,
  Languages,
  Anchor,
  MapPin,
  ChevronRight,
  Menu,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";

// 섹션 위쪽 어딘가에 미리 배열로 정의해 두고
const cruisePartnerLogos = [
  {
    name: "Cruise Company 1",
    logo: "/images/services/cruise/logos/logo1.png",
    url: "https://princesscruises.co.kr",
  },
  {
    name: "Cruise Company 2",
    logo: "/images/services/cruise/logos/logo2.png",
    url: "https://www.stardreamcruises.com",
  },
  {
    name: "Cruise Company 3",
    logo: "/images/services/cruise/logos/logo3.png",
    url: "https://disneycruise.disney.go.com",
  },
  {
    name: "Cruise Company 4",
    logo: "/images/services/cruise/logos/logo4.png",
    url: "https://www.royalcaribbean.com",
  },
  {
    name: "Cruise Company 5",
    logo: "/images/services/cruise/logos/logo5.png",
    url: "https://www.vikingcruises.com.au",
  },
];

/** ------------------------------
 *  공통 유틸: TOC 데이터/스크롤
 * -------------------------------- */
function useToc() {
  const { t } = useTranslation();
  const toc = useMemo(
    () => [
      {
        id: "about-cruise",
        label: t("cruise.what.title"),
        children: [
          { id: "cruise-future", label: t("cruise.future.title") },
          { id: "cruise-companies", label: t("cruise.companies.title") },
        ],
      },
      {
        id: "about-crew",
        label: t("cruise.who.title"),
        children: [
          { id: "crew-jobs", label: t("cruise.jobs.title") },
          { id: "crew-reqs", label: t("cruise.who.reqTitle") },
          { id: "crew-benefits", label: t("cruise.benefits.title") },
          { id: "crew-work", label: t("cruise.work.title") },
          { id: "crew-value", label: t("cruise.crewValue.title") },
        ],
      },
      {
        id: "how-to-join",
        label: t("cruise.flow.title"),
        children: [
          // { id: "join-flow", label: t("cruise.flow.title") },
          { id: "join-training-center", label: t("cruise.training.title") },
          // 면접 단계 제거
          { id: "join-why-malaysia", label: t("cruise.why.title") },
          { id: "join-internship", label: t("cruise.internship.title") },
          { id: "join-curriculum", label: t("cruise.curriculum.title") },
          { id: "join-docs", label: t("cruise.docs.title") },
        ],
      },
      {
        id: "stories",
        label: t("cruise.reviews.title"),
        children: [],
      },
    ],
    [t]
  );

  const scrollToId =
    (id, offset = 80) =>
    (e) => {
      if (e) e.preventDefault();
      const el = document.getElementById(id);
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: y, behavior: "smooth" });
    };

  return { toc, scrollToId };
}

/** ------------------------------
 *  공통: 이미지 (플레이스홀더 포함)
 * -------------------------------- */
function ImageBox({ src, alt = "", hint = "", className = "", imgClass = "" }) {
  const [err, setErr] = useState(false);
  return (
    <figure className={className}>
      {!err ? (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          className={`h-auto w-full rounded-2xl ring-1 ring-slate-200/60 object-cover shadow-[0_8px_20px_rgba(15,23,42,0.06)] ${imgClass}`}
          onError={() => setErr(true)}
        />
      ) : (
        <div className="grid aspect-[16/9] w-full place-items-center rounded-2xl border-2 border-dashed border-red-300 bg-gradient-to-br from-white to-red-50 p-4 text-center text-[12px] text-red-500 shadow-sm">
          <div>
            <div className="font-semibold">이미지 자리</div>
            <div className="mt-1 whitespace-pre-line opacity-80">{hint}</div>
          </div>
        </div>
      )}
      {hint && (
        <figcaption className="mt-2 text-center text-[11px] text-slate-500">
          {hint}
        </figcaption>
      )}
    </figure>
  );
}

/** 얇은 구분선 */
function Divider() {
  return (
    <div className="my-12 md:my-16">
      <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
    </div>
  );
}

/** 공통 소섹션 래퍼 (작은 제목) */
function Section({ id, title, desc, children }) {
  return (
    <section id={id} className="py-12">
      <Container>
        <div className="mx-auto max-w-3xl text-center">
          {title && (
            <h3 className="text-2xl font-bold tracking-tight md:text-3xl">
              {title}
            </h3>
          )}
          {desc && <p className="mt-2 text-slate-600">{desc}</p>}
        </div>
        <div className="mt-8">{children}</div>
      </Container>
    </section>
  );
}

/** 큰 섹션 래퍼 (굵은 제목) */
function BigSection({ id, title, desc, children }) {
  return (
    <section id={id} className="py-16 md:py-20">
      <Container>
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-extrabold tracking-tight md:text-5xl">
            {title}
          </h2>
          {desc && (
            <p className="mt-3 text-lg leading-7 text-slate-600">{desc}</p>
          )}
        </div>
        <div className="mt-10">{children}</div>
      </Container>
    </section>
  );
}

/** ------------------------------
 *  우측 고정 TOC (데스크톱) - 접기/펼치기 + 정확한 활성화
 * -------------------------------- */
function RightToc() {
  const { toc, scrollToId: baseScroll } = useToc();
  const { t } = useTranslation();
  const [active, setActive] = useState("");
  const [open, setOpen] = useState(true);

  // 스크롤 스파이: 뷰포트 상단에 가장 가까운 섹션을 활성화
  useEffect(() => {
    const ids = [
      ...toc.map((x) => x.id),
      ...toc.flatMap((x) => x.children?.map((c) => c.id) || []),
    ];
    const handle = () => {
      const top = window.scrollY + 90; // 헤더 보정
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const y = el.offsetTop;
        if (y <= top) current = id;
        else break;
      }
      setActive(current);
    };
    handle();
    window.addEventListener("scroll", handle, { passive: true });
    window.addEventListener("resize", handle);
    return () => {
      window.removeEventListener("scroll", handle);
      window.removeEventListener("resize", handle);
    };
  }, [toc]);

  // 클릭 시 즉시 활성화(시각 피드백) + 스크롤
  const scrollToId = (id) => (e) => {
    setActive(id);
    baseScroll(id, 80)(e);
  };

  const isParentActive = (parent) =>
    active === parent.id ||
    (parent.children || []).some((c) => c.id === active);

  // 접힘 상태일 때 토글 버튼만 노출
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed right-4 top-28 z-40 hidden h-10 w-10 items-center justify-center rounded-full bg-white text-slate-700 shadow-lg ring-1 ring-slate-200 lg:flex"
        aria-label="Open table of contents"
        title="TOC 열기"
      >
        <Menu className="h-5 w-5" />
      </button>
    );
  }

  return (
    <aside
      className="fixed right-4 top-28 z-40 hidden w-[280px] lg:block"
      aria-label="Page table of contents"
    >
      <div className="rounded-2xl border bg-white/95 backdrop-blur shadow-[0_8px_24px_rgba(15,23,42,0.08)] ring-1 ring-slate-200">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <Ship className="h-4 w-4 text-sky-600" />
            <span className="text-sm font-semibold text-slate-800">목차</span>
          </div>
          <button
            aria-label="Close TOC"
            className="rounded-md p-1.5 hover:bg-slate-100"
            onClick={() => setOpen(false)}
            title="TOC 접기"
          >
            <X className="h-5 w-5 text-slate-600" />
          </button>
        </div>

        <nav className="max-h-[70vh] overflow-y-auto p-3">
          <ul className="space-y-2">
            {toc.map((sec) => (
              <li key={sec.id}>
                <a
                  href={`#${sec.id}`}
                  onClick={scrollToId(sec.id)}
                  className={`group flex items-start gap-2 rounded-lg px-2 py-1.5 text-sm transition ${
                    isParentActive(sec)
                      ? "bg-sky-50 text-sky-700 ring-1 ring-sky-100"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <ChevronRight
                    className={`mt-0.5 h-4 w-4 shrink-0 transition ${
                      isParentActive(sec) ? "rotate-90 text-sky-600" : ""
                    }`}
                  />
                  <span className="leading-5">{sec.label}</span>
                </a>

                {sec.children?.length ? (
                  <ul className="mt-1 ml-6 space-y-1">
                    {sec.children.map((child) => (
                      <li key={child.id}>
                        <a
                          href={`#${child.id}`}
                          onClick={scrollToId(child.id)}
                          className={`flex items-start gap-2 rounded-md px-2 py-1 text-[13px] leading-5 transition ${
                            active === child.id
                              ? "bg-sky-50 text-sky-700 ring-1 ring-sky-100"
                              : "text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-current opacity-60" />
                          {child.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </aside>
  );
}

/** ------------------------------
 *  모바일 TOC: FAB + 드로어
 * -------------------------------- */
function MobileToc() {
  const { toc, scrollToId: baseScroll } = useToc();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");

  // 스크롤 스파이(모바일도 동일 로직)
  useEffect(() => {
    const ids = [
      ...toc.map((x) => x.id),
      ...toc.flatMap((x) => x.children?.map((c) => c.id) || []),
    ];
    const handle = () => {
      const top = window.scrollY + 90;
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.offsetTop <= top) current = id;
        else break;
      }
      setActive(current);
    };
    handle();
    window.addEventListener("scroll", handle, { passive: true });
    window.addEventListener("resize", handle);
    return () => {
      window.removeEventListener("scroll", handle);
      window.removeEventListener("resize", handle);
    };
  }, [toc]);

  const handleClick = (id) => (e) => {
    setActive(id);
    baseScroll(id, 80)(e);
    setOpen(false);
  };

  return (
    <>
      {/* FAB */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-4 z-40 grid h-12 w-12 place-items-center rounded-full bg-sky-600 text-white shadow-lg ring-1 ring-sky-300 lg:hidden"
        aria-label="Open table of contents"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Overlay + Drawer */}
      <div
        className={`fixed inset-0 z-50 transition lg:hidden ${
          open ? "pointer-events-auto" : "pointer-events-none"
        }`}
        aria-hidden={!open}
      >
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity ${
            open ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setOpen(false)}
        />
        <div
          className={`absolute bottom-0 left-0 right-0 rounded-t-2xl bg-white shadow-2xl ring-1 ring-slate-200 transition-transform ${
            open ? "translate-y-0" : "translate-y-full"
          }`}
        >
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div className="flex items-center gap-2">
              <Ship className="h-4 w-4 text-sky-600" />
              <span className="text-sm font-semibold text-slate-800">
                Index
              </span>
            </div>
            <button
              aria-label="Close"
              className="rounded-md p-1.5 hover:bg-slate-100"
              onClick={() => setOpen(false)}
            >
              <X className="h-5 w-5 text-slate-600" />
            </button>
          </div>

          <nav className="max-h-[60vh] overflow-y-auto p-3">
            <ul className="space-y-2">
              {toc.map((sec) => (
                <li key={sec.id}>
                  <a
                    href={`#${sec.id}`}
                    onClick={handleClick(sec.id)}
                    className={`group flex items-start gap-2 rounded-lg px-2 py-2 text-sm transition ${
                      active === sec.id
                        ? "bg-sky-50 text-sky-700 ring-1 ring-sky-100"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <ChevronRight
                      className={`mt-0.5 h-4 w-4 shrink-0 transition ${
                        active === sec.id ? "rotate-90 text-sky-600" : ""
                      }`}
                    />
                    <span className="leading-5">{sec.label}</span>
                  </a>

                  {sec.children?.length ? (
                    <ul className="mt-1 ml-6 space-y-1">
                      {sec.children.map((child) => (
                        <li key={child.id}>
                          <a
                            href={`#${child.id}`}
                            onClick={handleClick(child.id)}
                            className={`flex items-start gap-2 rounded-md px-2 py-1.5 text-[13px] leading-5 transition ${
                              active === child.id
                                ? "bg-sky-50 text-sky-700 ring-1 ring-sky-100"
                                : "text-slate-600 hover:bg-slate-50"
                            }`}
                          >
                            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-current opacity-60" />
                            {child.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
}

/** ====== PAGE (Storytelling order) ====== */
export default function Cruise() {
  return (
    <div className="text-blue-900">
      <RightToc /> {/* 데스크톱 고정 TOC (접기 지원) */}
      <MobileToc /> {/* 모바일 플로팅/드로어 TOC */}
      <Intro /> {/* 0. 풀스크린 인트로 */}
      <CruiseAbout /> {/* 1. 크루즈란? */}
      <CrewAbout /> {/* 2. 크루즈 승무원이란? */}
      <HowToJoin /> {/* 3. 입사 방법 */}
      <CTA /> {/* 최종 CTA */}
    </div>
  );
}

/* 0) Intro — 메인배너 (크루즈 톤) */
function Intro() {
  const { t } = useTranslation();
  return (
    <section className="relative min-h-[100dvh] overflow-hidden">
      <img
        src="/images/home/intro/hero2.jpg"
        alt="Cruise crew working onboard"
        aria-hidden="true"
        className="absolute inset-0 z-0 block h-full w-full object-cover object-center"
        loading="lazy"
        decoding="async"
      />
      <div className="absolute inset-0 z-10 bg-[radial-gradient(ellipse_at_center,rgba(2,132,199,0.25),transparent_60%)]" />
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-blue-950/70 via-blue-900/55 to-blue-900/30" />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-0 right-0 z-20 h-24 bg-gradient-to-b from-transparent to-white"
      />
      <Container className="relative z-30 flex min-h-[100dvh] flex-col justify-center py-16">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1 text-[11px] font-medium text-blue-700 shadow-sm ring-1 ring-slate-200">
            <Ship className="h-3.5 w-3.5" />
            {t("home.intro.badge")}
          </span>
          <h1 className="mt-4 text-4xl font-extrabold tracking-tight text-white drop-shadow md:text-6xl">
            {t("home.intro.title")}
          </h1>
          <p className="mt-4 max-w-prose text-blue-50 md:text-lg">
            {t("home.intro.desc")}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="/contact-us"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-blue-700 shadow-sm ring-1 ring-slate-200 transition hover:-translate-y-0.5 hover:bg-blue-50"
            >
              {t("home.intro.ctaPrimary")} <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </Container>
    </section>
  );
}

/* ========== 큰섹션 #1: 크루즈란? ========== */
function CruiseAbout() {
  const { t } = useTranslation();

  return (
    <BigSection
      id="about-cruise"
      title={t("cruise.what.title")}
      desc={t("cruise.what.desc")}
    >
      <Divider />

      {/* 크루즈 설명 + 특징/종류/장점 + 우측 세로 이미지 */}
      <div className="grid items-start gap-8 md:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
        {/* 왼쪽: 텍스트 블록 */}
        <div className="space-y-6 text-[15px] leading-7 text-slate-700 md:text-base">
          {/* 간단 정의(한 단락) */}
          <p>{t("cruise.what.intro")}</p>

          {/* 크루즈의 특징 */}
          <section className="space-y-2">
            <h3 className="font-semibold text-slate-900">
              {t("cruise.what.featuresTitle")}
            </h3>
            <ul className="list-disc space-y-1 pl-5">
              <li>{t("cruise.what.feature1")}</li>
              <li>{t("cruise.what.feature2")}</li>
              <li>{t("cruise.what.feature3")}</li>
              <li>{t("cruise.what.feature4")}</li>
            </ul>
          </section>

          {/* 주요 크루즈 종류 */}
          <section className="space-y-2">
            <h3 className="font-semibold text-slate-900">
              {t("cruise.what.typesTitle")}
            </h3>
            <ol className="list-decimal space-y-2 pl-5">
              <li>
                <span className="font-medium">
                  {t("cruise.what.type1Title")}
                </span>{" "}
                {t("cruise.what.type1Desc")}
              </li>
              <li>
                <span className="font-medium">
                  {t("cruise.what.type2Title")}
                </span>{" "}
                {t("cruise.what.type2Desc")}
              </li>
              <li>
                <span className="font-medium">
                  {t("cruise.what.type3Title")}
                </span>{" "}
                {t("cruise.what.type3Desc")}
              </li>
              <li>
                <span className="font-medium">
                  {t("cruise.what.type4Title")}
                </span>{" "}
                {t("cruise.what.type4Desc")}
              </li>
            </ol>
          </section>

          {/* 크루즈 여행의 장점 */}
          <section className="space-y-2">
            <h3 className="font-semibold text-slate-900">
              {t("cruise.what.benefitsTitle")}
            </h3>
            <ul className="list-disc space-y-1 pl-5">
              <li>{t("cruise.what.benefit1")}</li>
              <li>{t("cruise.what.benefit2")}</li>
              <li>{t("cruise.what.benefit3")}</li>
            </ul>
          </section>
        </div>

        {/* 오른쪽: 세로(포트레이트) 이미지 */}
        <div className="mx-auto w-full max-w-xs md:max-w-sm">
          <ImageBox
            src="/images/services/cruise/hero.jpg"
            imgClass="aspect-[3/4] md:aspect-[3/4] object-cover rounded-2xl"
          />
        </div>
      </div>

      {/* 1-2) 크루즈 산업 전망 — 여백/가독성 강화 */}
      <Section
        id="cruise-future"
        title={t("cruise.future.title")}
        desc={t("cruise.future.p1")}
      >
        <div className="rounded-3xl bg-gradient-to-br from-sky-50 to-white p-6 ring-1 ring-sky-100 md:p-8">
          <div className="grid items-start gap-12 md:grid-cols-[1.1fr,0.9fr]">
            <div className="space-y-5 text-[15px] leading-8 text-slate-700 md:text-base">
              <p>{t("cruise.future.p2")}</p>
              <p>{t("cruise.future.p3")}</p>
            </div>
            <ImageBox
              src="/images/services/cruise/graph.png"
              imgClass="aspect-[16/9]"
            />
          </div>
        </div>
      </Section>

      {/* 1-3) 유명 크루즈 선사 소개 — 업로드 콜라주 활용 */}
      {/* <Section id="cruise-companies" title={t("cruise.companies.title")}>
        <div>
          <ImageBox
            src={"/images/services/cruise/cruise.png"}
            alt="International Cruise Liners (35개사)"
          />
        </div>
      </Section> */}

      {/* 1-3) 유명 크루즈 선사 소개 — 업로드 콜라주 + 로고 */}
      <Section id="cruise-companies" title={t("cruise.companies.title")}>
        <div className="space-y-8">
          {/* 파트너 로고 리스트 */}
          <div className="rounded-2xl bg-white/70 p-4 shadow-sm">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
              {cruisePartnerLogos.map((c) => (
                <a
                  key={c.name}
                  href={c.url}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex items-center justify-center rounded-xl bg-slate-50 px-3 py-3 transition hover:bg-slate-100"
                >
                  <img
                    src={c.logo}
                    alt={c.name}
                    className="h-10 w-auto object-contain"
                  />
                </a>
              ))}
            </div>
          </div>
        </div>
      </Section>
      <Section id="cruise-companies" title={t("cruise.companies2.title")}>
        <div>
          <ImageBox
            src={"/images/services/cruise/cruise.png"}
            alt="International Cruise Liners (35개사)"
          />
        </div>
      </Section>
    </BigSection>
  );
}

/* ========== 큰섹션 #2: 크루즈 승무원이란? ========== */
function CrewAbout() {
  const { t } = useTranslation();
  const groups = t("cruise.jobs.groups", { returnObjects: true }) || [];
  const duties = t("cruise.who.duties", { returnObjects: true }) || [];
  const reqs = t("cruise.who.requirements", { returnObjects: true }) || [];
  const benefits = t("cruise.benefits.list", { returnObjects: true }) || [];
  const bullets = t("cruise.work.bullets", { returnObjects: true }) || [];
  const valueList = t("cruise.crewValue.list", { returnObjects: true }) || [];

  // 🔹 직무 카드 썸네일 이미지들 (순서대로 1~4번 카드)
  const jobImages = [
    "/images/services/cruise/jobs/hotel.jpg",
    "/images/services/cruise/jobs/service.jpg",
    "/images/services/cruise/jobs/restaurant.jpg",
    "/images/services/cruise/jobs/entertain.jpg",
    "/images/services/cruise/jobs/ship.jpg",
    "/images/services/cruise/jobs/excursions.jpg",
  ];

  const groupsWithImage =
    groups.map((g, idx) => ({
      ...g,
      image: jobImages[idx] || null,
    })) ?? [];

  return (
    <BigSection
      id="about-crew"
      title={t("cruise.who.title")}
      desc={t("cruise.who.desc")}
    >
      {/* 2-1) 직무 카테고리 */}
      <Section
        id="crew-jobs"
        title={t("cruise.jobs.title")}
        desc={t("cruise.jobs.desc")}
      >
        <div className="mx-auto grid max-w-4xl grid-cols-1 gap-4 md:grid-cols-2">
          {groupsWithImage.map((g, i) => (
            <div
              key={i}
              className="rounded-2xl border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              {/* 🔹 카드 상단 풀폭 이미지 */}
              {g.image && (
                <div className="mb-3 overflow-hidden rounded-2xl">
                  <img
                    src={g.image}
                    alt={g.title}
                    className="h-40 w-full object-cover" // 필요하면 h-40 / h-48 등으로 조절
                  />
                </div>
              )}

              {/* 🔹 텍스트 영역 */}
              <div>
                <div className="flex items-center gap-2">
                  <Anchor className="h-4 w-4 text-sky-600" />
                  <div className="font-semibold">{g.title}</div>
                </div>
                <p className="mt-1 text-sm text-slate-600">
                  {Array.isArray(g.items) ? g.items.join(" · ") : g.items}
                </p>
              </div>
            </div>
          ))}
        </div>

        {t("cruise.jobs.note") && (
          <p className="mt-4 text-center text-xs text-slate-500">
            {t("cruise.jobs.note")}
          </p>
        )}
      </Section>

      {/* 2-2) 승무원 역할/조건 — (소제목 제거 유지) */}
      <Section id="crew-reqs">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              {t("cruise.who.dutiesTitle")}
            </div>
            <ul className="mt-3 grid gap-2 text-sm">
              {duties.map((d) => (
                <li key={d} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
                  <span className="text-slate-700">{d}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <Languages className="h-5 w-5 text-indigo-600" />
              {t("cruise.who.reqTitle")}
            </div>
            <ul className="mt-3 grid gap-2 text-sm">
              {reqs.map((r) => (
                <li key={r} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
                  <span className="text-slate-700">{r}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <p className="mt-4 text-center text-xs text-slate-500">
          {t("cruise.who.note")}
        </p>
      </Section>

      {/* 2-3) 복지/근무 조건 */}
      <Section id="crew-benefits" title={t("cruise.benefits.title")}>
        <ul className="mx-auto grid max-w-3xl grid-cols-1 gap-3 text-sm md:grid-cols-2">
          {benefits.map((d) => (
            <li
              key={d}
              className="flex items-start gap-2 rounded-xl border bg-white p-3 shadow-sm transition hover:shadow-md"
            >
              <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
              <span className="text-slate-700">{d}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section id="crew-work" title={t("cruise.work.title")}>
        <div className="mx-auto grid max-w-4xl grid-cols-1 gap-3">
          {bullets.map((d) => (
            <div
              key={d}
              className="flex items-start gap-2 rounded-xl border bg-white p-3 text-sm shadow-sm transition hover:shadow-md"
            >
              <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
              <span className="text-slate-700">{d}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* 2-4) 승무원의 전망/매력 */}
      <Section id="crew-value" title={t("cruise.crewValue.title")}>
        <ul className="mx-auto grid max-w-3xl grid-cols-1 gap-3 text-sm">
          {valueList.map((d) => (
            <li
              key={d}
              className="flex items-start gap-2 rounded-xl border bg-white p-3 shadow-sm transition hover:shadow-md"
            >
              <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
              <span className="text-slate-700">{d}</span>
            </li>
          ))}
        </ul>
      </Section>
    </BigSection>
  );
}

/* ========== 큰섹션 #3: 입사 방법 ========== */
function HowToJoin() {
  const { t } = useTranslation();
  const steps = t("cruise.flow.steps", { returnObjects: true }) || [];
  const cards = t("cruise.training.cards", { returnObjects: true }) || [];

  // 🔹 트레이닝 카드 썸네일 이미지들 (순서대로 1,2,3...)
  const trainingImages = [
    "/images/services/cruise/infinity.png",
    "/images/services/cruise/training.png",
    "/images/services/cruise/test.jpg",
    "/images/services/cruise/intern.png",
  ];

  const cardsWithImage = cards.map((c, idx) => ({
    ...c,
    image: trainingImages[idx] || null,
  }));

  return (
    <BigSection
      id="how-to-join"
      title={t("cruise.flow.title")}
      desc={t("cruise.flow.desc")}
    >
      {/* 3-1) 회사 프로세스(STEP 1~6) */}
      <Section id="join-flow" desc={t("cruise.flow.note")}>
        <ol className="mx-auto grid max-w-5xl grid-cols-1 gap-3 md:grid-cols-6">
          {steps.map((s, i) => (
            <li
              key={i}
              className="rounded-2xl border bg-white p-4 text-sm shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <span className="mb-1 inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] text-blue-700 ring-1 ring-blue-100">
                <Clock className="h-3.5 w-3.5" /> STEP {i + 1}
              </span>
              <div className="font-medium">{s}</div>
            </li>
          ))}
        </ol>
      </Section>

      {/* 3-2) Infinity(말레이시아) 소개 */}
      <Section
        id="join-training-center"
        title={t("cruise.training.title")}
        desc={t("cruise.training.desc")}
      >
        <div className="mx-auto grid max-w-4xl grid-cols-1 gap-4 md:grid-cols-2">
          {cardsWithImage.map((b, i) => (
            <div
              key={i}
              className="rounded-2xl border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              {/* 🔹 카드 상단 풀폭 이미지 */}
              {b.image && (
                <div className="mb-3 overflow-hidden rounded-2xl">
                  <img
                    src={b.image}
                    alt={b.h}
                    className="h-64 w-full object-cover" // 필요하면 h-40 / h-48 로 조절
                  />
                </div>
              )}

              {/* 🔹 텍스트 영역 */}
              <div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-sky-600" />
                  <div className="font-semibold">{b.h}</div>
                </div>
                <p className="mt-1 text-sm text-slate-600">{b.d}</p>
              </div>
            </div>
          ))}
        </div>

        {t("cruise.training.note") && (
          <p className="mt-4 text-center text-xs text-slate-500">
            {t("cruise.training.note")}
          </p>
        )}
      </Section>

      {/* 3-3) 왜 말레이시아인가 — 교육기관 섹션 바로 다음으로 이동 */}
      <Section
        id="join-why-malaysia"
        title={t("cruise.why.title")}
        desc={t("cruise.why.desc")}
      >
        <div className="grid items-start gap-8 md:grid-cols-[1.2fr,0.8fr]">
          <div className="space-y-3 text-sm leading-7 text-slate-700">
            <p>{t("cruise.why.p1")}</p>
            <p>{t("cruise.why.p2")}</p>
            <ul className="mt-3 grid gap-2">
              {(t("cruise.why.points", { returnObjects: true }) || []).map(
                (x) => (
                  <li key={x} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
                    <span className="text-slate-700">{x}</span>
                  </li>
                )
              )}
            </ul>
          </div>
          <ImageBox
            src="/images/services/cruise/why-malaysia.jpg"
            imgClass="aspect-[4/3]"
          />
        </div>
      </Section>

      {/* 3-4) 영어·경력 해결 (인턴십/커리큘럼) */}
      <Section id="join-internship" title={t("cruise.internship.title")}>
        <div className="rounded-3xl border bg-white p-6 shadow-sm md:p-8">
          <div className="grid items-center gap-8 md:grid-cols-12">
            <div className="md:col-span-7">
              <div className="mx-auto max-w-[56ch] text-[15px] leading-7 text-slate-700 md:text-base">
                <p>{t("cruise.internship.p1")}</p>
                <p className="mt-3">{t("cruise.internship.p2")}</p>
              </div>
            </div>
            <div className="md:col-span-5">
              <ImageBox
                src="/images/services/cruise/internship.jpg"
                imgClass="aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </Section>

      <Section
        id="join-curriculum"
        title={t("cruise.curriculum.title")}
        desc={t("cruise.curriculum.desc")}
      >
        <CurriculumOnly />
      </Section>

      {/* 3-5) 합격자 필수 서류 */}
      <Section
        id="join-docs"
        title={t("cruise.docs.title")}
        desc={t("cruise.docs.desc")}
      >
        <DocsOnly />
      </Section>
    </BigSection>
  );
}

/* ====== 소컴포넌트(재사용 파트) ====== */

/* 커리큘럼 카드들만 단독으로 렌더 */
function CurriculumOnly() {
  const { t } = useTranslation();
  const tracks = t("cruise.curriculum.tracks", { returnObjects: true }) || [];
  return (
    <div className="mx-auto grid max-w-6xl grid-cols-1 gap-4 md:grid-cols-3">
      {tracks.map((trk) => (
        <div
          key={trk.title}
          className="rounded-2xl border bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
        >
          <div className="text-lg font-semibold">{trk.title}</div>
          <ul className="mt-2 grid gap-1 text-sm text-slate-700">
            {trk.items.map((i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
                <span>{i}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {t("cruise.curriculum.note") && (
        <p className="col-span-full mt-4 text-center text-xs text-slate-500">
          {t("cruise.curriculum.note")}
        </p>
      )}
    </div>
  );
}

/* 문서 리스트만 단독으로 렌더 */
function DocsOnly() {
  const { t } = useTranslation();
  const docs = t("cruise.docs.list", { returnObjects: true }) || [];
  return (
    <>
      <ul className="mx-auto grid max-w-3xl grid-cols-1 gap-3 text-sm md:grid-cols-2">
        {docs.map((d) => (
          <li
            key={d}
            className="flex items-start gap-2 rounded-xl border bg-white p-3 shadow-sm transition hover:shadow-md"
          >
            <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
            <span className="text-slate-700">{d}</span>
          </li>
        ))}
      </ul>
      {t("cruise.docs.note") && (
        <p className="mt-3 text-center text-xs text-slate-500">
          {t("cruise.docs.note")}
        </p>
      )}
    </>
  );
}

/* CTA */
function CTA() {
  const { t } = useTranslation();
  return (
    <section className="py-16">
      <Container>
        <div className="relative isolate overflow-hidden rounded-3xl border text-white shadow-[0_12px_36px_rgba(2,132,199,0.25)] ring-1 ring-sky-200">
          <img
            src="/images/home/cta/cta-background.jpg"
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 -z-10 h-full w-full object-cover"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 -z-10 bg-gradient-to-br from-blue-600/90 via-blue-500/85 to-sky-500/80"
          />
          <div className="relative z-10 p-8 md:p-12">
            <h3 className="text-2xl font-bold md:text-3xl">
              {t("cruise.cta.title")}
            </h3>
            <p className="mt-2 max-w-prose text-blue-50">
              {t("cruise.cta.desc")}
            </p>
            <div className="mt-6">
              <a
                href="/contact-us"
                className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-medium text-blue-700 shadow-sm ring-1 ring-slate-200 transition hover:-translate-y-0.5 hover:bg-blue-50"
              >
                {t("cruise.cta.button")} <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
